import React, { useState, useEffect } from "react";
import { Box, TextField, Chip, Grid } from "@mui/material";
import { RequiredIndicator } from "./FormFields";

interface Parameter {
  id: string;
  parameter_name: string;
  display_name: string;
  description: string;
  parameter_type: string;
  is_required: boolean;
  min_value: string | null;
  max_value: string | null;
}

interface MultiNumberInputProps {
  label?: string;
  parameters?: Parameter[];
  value?: Record<string, number>;
  onChange?: (value: Record<string, number>) => void;
  props?: any;
}

const MultiNumberInput: React.FC<MultiNumberInputProps> = ({
  label,
  parameters = [],
  value = {},
  onChange,
  props,
}) => {
  const [paramValues, setParamValues] = useState<Record<string, string>>(
    Object.fromEntries(
      Object.entries(value).map(([key, val]) => [key, String(val)]),
    ),
  );

  useEffect(() => {
    setParamValues(
      Object.fromEntries(
        Object.entries(value).map(([key, val]) => [key, String(val)]),
      ),
    );
  }, [value]);

  const handleChange = (paramName: string, inputValue: string) => {
    const newValues = { ...paramValues, [paramName]: inputValue };
    setParamValues(newValues);

    // Convert to numbers and call onChange
    const numericValues: Record<string, number> = {};
    Object.entries(newValues).forEach(([key, val]) => {
      const num = parseFloat(val);
      if (!isNaN(num) && val !== "") {
        numericValues[key] = num;
      }
    });

    onChange?.(numericValues);
  };

  const handleRemove = (paramName: string) => {
    const newValues = { ...paramValues };
    delete newValues[paramName];
    setParamValues(newValues);

    const numericValues: Record<string, number> = {};
    Object.entries(newValues).forEach(([key, val]) => {
      const num = parseFloat(val);
      if (!isNaN(num) && val !== "") {
        numericValues[key] = num;
      }
    });

    onChange?.(numericValues);
  };

  if (!parameters || parameters.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: "center", color: "#999" }}>
        <p className="font12">Select a template to configure parameters</p>
      </Box>
    );
  }

  return (
    <Box>
      {label && (
        <p className="capitalize font12 mb-1 mx-1">
          {label} <RequiredIndicator required={props?.required} />
        </p>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {parameters.map((param) => {
          const min = param.min_value ? parseFloat(param.min_value) : undefined;
          const max = param.max_value ? parseFloat(param.max_value) : undefined;
          const isInteger = param.parameter_type === "INTEGER";

          return (
            <Box key={param.id}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <Box>
                    <p className="font12 bold mb-0">
                      {param.display_name}
                      {param.is_required && (
                        <span style={{ color: "red" }}> *</span>
                      )}
                    </p>
                    <p className="font10 mb-0" style={{ color: "#666" }}>
                      {param.description}
                    </p>
                    {(min !== undefined || max !== undefined) && (
                      <p className="font10 mb-0" style={{ color: "#999" }}>
                        Range: {min ?? "∞"} - {max ?? "∞"}
                      </p>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={paramValues[param.parameter_name] || ""}
                    onChange={(e) =>
                      handleChange(param.parameter_name, e.target.value)
                    }
                    placeholder={`Enter ${param.display_name.toLowerCase()}`}
                    inputProps={{
                      min,
                      max,
                      step: isInteger ? 1 : 0.1,
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          );
        })}
      </Box>

      {/* Display current values as chips */}
      {Object.keys(paramValues).length > 0 && (
        <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
          {Object.entries(paramValues).map(([key, val]) => {
            const param = parameters.find((p) => p.parameter_name === key);
            if (!val || !param) return null;

            return (
              <Chip
                key={key}
                label={`${param.display_name}: ${val}`}
                onDelete={() => handleRemove(key)}
                size="small"
                sx={{
                  backgroundColor: "#e6f7ff",
                  border: "1px solid #91d5ff",
                  fontSize: "11px",
                }}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default MultiNumberInput;
